import {
    getRelatedAffinitiesGoogleData,
    getRelatedBrandsGoogleData,
} from '../api/google.api';
import { shapeDataPoint } from '../../state/utils';
import { useInfiniteQuery } from 'react-query';

const apiFn = (paginateBy) =>
    paginateBy === 'affinities'
        ? getRelatedAffinitiesGoogleData
        : getRelatedBrandsGoogleData;

const queryFn = async ({ queryKey, pageParam = 1 }) => {
    const {
        paginateBy,
        order_by,
        ascending,
        country,
        category,
        brand,
        per_page,
        search,
        affinity_group,
        cluster,
        price_segmentation,
    } = queryKey[1];
    const data = await apiFn(paginateBy)({
        data: {
            category,
            brand,
            country,
            order_by,
            ascending,
            per_page,
            page: pageParam,
            search,
            affinity_group,
            cluster,
            price_segmentation,
        },
    });
    return {
        ...data,
        data: data?.data?.map((record) => {
            const {
                affinity_name,
                affinity_id,
                affinity_group,
                brand_name,
                brand_id,
            } = record;
            const id = affinity_id || brand_id;
            const name = affinity_name || brand_name;
            const shaped = shapeDataPoint(record);
            return {
                ...shaped,
                parentName: affinity_group || null,
                searchField: name,
                name,
                id,
                dataPointId: record.id,
            };
        }),
    };
};

export const getQueryKeyGoogleRelatedEntityData = ({
    order_by,
    ascending,
    categories,
    brands,
    countries,
    search,
    affinity_group,
    cluster,
    price_segmentation,
    per_page,
    paginateBy,
}) => {
    return [
        'GoogleRelatedEntityData',
        {
            order_by,
            ascending,
            category: categories,
            brand: brands,
            country: countries,
            search,
            affinity_group,
            cluster,
            price_segmentation,
            per_page,
            paginateBy,
        },
    ];
};

const useGoogleRelatedEntities = ({
    per_page = 500,
    order_by = 'volume',
    ascending,
    paginateBy,
    brands,
    categories,
    countries,
    search,
    affinity_group,
    cluster,
    price_segmentation,
    enabled = true,
    placeholderData,
    keepPreviousData = true,
}) => {
    const queryKey = getQueryKeyGoogleRelatedEntityData({
        order_by,
        ascending,
        categories,
        brands,
        countries,
        search,
        affinity_group,
        cluster,
        price_segmentation,
        per_page,
        paginateBy,
    });
    const query = useInfiniteQuery(queryKey, queryFn, {
        enabled,
        staleTime: Infinity,
        getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
        keepPreviousData,
        placeholderData: placeholderData?.length
            ? () => ({
                  pages: [
                      {
                          data: placeholderData,
                          total_records: placeholderData?.length || 0,
                          last_page: 1,
                          messages: null,
                          next_page: null,
                          page: 1,
                          per_page,
                          prev_page: null,
                      },
                  ],
              })
            : undefined,
    });
    const getQueryData = async () => {
        const currentPages = query?.data?.pages || [];
        const proms = [];
        for (
            let i = currentPages.length + 1;
            i <= query?.data?.pages[0]?.last_page;
            i++
        ) {
            proms.push(queryFn({ queryKey, pageParam: i }));
        }
        const restPages = await Promise.all(proms);
        return [...currentPages, ...restPages].map(({ data }) => data).flat();
    };
    return {
        query,
        queryKey,
        getQueryData,
    };
};

export default useGoogleRelatedEntities;
